import { useState, useRef, useEffect, useCallback } from "react";
import { Package, ChevronDown, X } from "lucide-react";
import { useQuery } from "react-query";
import { apiService } from "../services/api";

interface ProductOption {
  id: string;
  name: string;
  businessArea: string;
  division: string;
}

interface TypeaheadProductInputProps {
  value: string;
  onChange: (productId: string, productName: string) => void;
  excludeIds?: string[];
  placeholder?: string;
  className?: string;
  disabled?: boolean;
}

export default function TypeaheadProductInput({
  value,
  onChange,
  excludeIds = [],
  placeholder = "Search for a product...",
  className = "",
  disabled = false,
}: TypeaheadProductInputProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => setDebouncedSearch(searchTerm), 300);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Fetch products matching search
  const { data: productsResponse, isLoading } = useQuery(
    ["productSearch", debouncedSearch],
    () =>
      apiService.getProducts({
        search: debouncedSearch || undefined,
        limit: 15,
        page: 1,
      }),
    { enabled: isOpen, keepPreviousData: true },
  );

  const products: ProductOption[] =
    (productsResponse?.data as any)?.data?.products
      ?.filter((p: ProductOption) => !excludeIds.includes(p.id))
      .slice(0, 10) ?? [];

  // Resolve display name from value (product ID)
  const { data: selectedProductResponse } = useQuery(
    ["product", value],
    () => apiService.getProduct(value),
    {
      enabled: !!value,
    },
  );

  const selectedProduct = value
    ? (selectedProductResponse?.data as any)?.data
    : null;

  const displayValue = selectedProduct ? selectedProduct.name : "";

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node) &&
        inputRef.current &&
        !inputRef.current.contains(event.target as Node)
      ) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      setSearchTerm(e.target.value);
      setIsOpen(true);
    },
    [],
  );

  const handleProductSelect = useCallback(
    (product: ProductOption) => {
      onChange(product.id, product.name);
      setSearchTerm("");
      setIsOpen(false);
    },
    [onChange],
  );

  const handleClear = useCallback(() => {
    onChange("", "");
    setSearchTerm("");
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, [onChange]);

  const handleFocus = useCallback(() => {
    setIsOpen(true);
  }, []);

  const handleKeyDown = useCallback((e: React.KeyboardEvent) => {
    if (e.key === "Escape") {
      setIsOpen(false);
    }
  }, []);

  return (
    <div className={`relative ${className}`}>
      <div className="relative">
        {value && displayValue ? (
          <div className="input pr-16 flex items-center">
            <Package className="h-4 w-4 text-gray-400 mr-2 flex-shrink-0" />
            <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
              {displayValue}
            </span>
          </div>
        ) : (
          <input
            ref={inputRef}
            type="text"
            value={searchTerm}
            onChange={handleInputChange}
            onFocus={handleFocus}
            onKeyDown={handleKeyDown}
            disabled={disabled}
            className="input pr-16"
            placeholder={placeholder}
            autoComplete="off"
          />
        )}

        <div className="absolute inset-y-0 right-0 flex items-center">
          {(value || searchTerm) && !disabled && (
            <button
              type="button"
              onClick={handleClear}
              className="p-1 text-gray-400 hover:text-gray-600 mr-1"
            >
              <X className="h-4 w-4" />
            </button>
          )}
          {!value && (
            <div className="p-2 text-gray-400">
              <ChevronDown
                className={`h-4 w-4 transition-transform ${isOpen ? "rotate-180" : ""}`}
              />
            </div>
          )}
        </div>
      </div>

      {isOpen && !disabled && !value && (
        <div
          ref={dropdownRef}
          className="absolute z-50 w-full mt-1 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md shadow-lg max-h-60 overflow-auto"
        >
          {isLoading ? (
            <div className="p-3 text-center text-gray-500">
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600 mx-auto mb-2"></div>
              Searching...
            </div>
          ) : products.length > 0 ? (
            products.map((product) => (
              <button
                key={product.id}
                type="button"
                onClick={() => handleProductSelect(product)}
                className="w-full px-3 py-2 text-left hover:bg-gray-100 dark:hover:bg-gray-700 flex items-center space-x-2 text-sm"
              >
                <Package className="h-4 w-4 text-gray-400 flex-shrink-0" />
                <div className="min-w-0">
                  <div className="text-gray-900 dark:text-gray-100 truncate">
                    {product.name}
                  </div>
                  <div className="text-gray-500 dark:text-gray-400 text-xs truncate">
                    {product.businessArea} &middot; {product.division}
                  </div>
                </div>
              </button>
            ))
          ) : (
            <div className="p-3 text-center text-gray-500 dark:text-gray-400 text-sm">
              {searchTerm
                ? "No products found"
                : "Start typing to search products"}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
